// Shared shaping for Activity-ledger rows.
//
// Two callers build the same row shape: useInvestmentData (one household —
// the investor's Activity tab and Home feed) and useAdminActivity (every
// household — the admin's Activity screen). Keeping the shaping here means a
// row reads the same on both surfaces and the filters (activityFilters.js)
// key off one set of facts: srcKind / dstKind slugs + labels, amount moved,
// eventDate, value, D21 custody, who logged it, and — for the admin view —
// household.
//
// ctx: {
//   kindOfAccount:  { accountId → account_kind slug }
//   stepsById:      { stepId → step row } (fallback route when no movement)
//   kindLabel:      slug → friendly label
//   dateLabel:      iso → 'Sep 3'
//   memberOf(mv):   which member a SILENT movement belongs to (household
//                   view: the holder; admin view: the owning account's member)
//   householdOf(m): the enrolled (parent) member id for a member id
// }

// The two account kinds an event moved between — the movement's own accounts
// when it has one (the record), the step's classification otherwise.
export function flowKindsOf(e, mvAccounts, ctx) {
  const mv = mvAccounts[e.movement_id];
  const src = mv && ctx.kindOfAccount[mv.source_account_id];
  const dst = mv && ctx.kindOfAccount[mv.dest_account_id];
  if (src && dst) return { src, dst };
  const st = ctx.stepsById[e.step_id];
  return { src: st?.source_kind || null, dst: st?.dest_kind || null };
}

export function flowFieldsOf({ src, dst }, ctx) {
  return {
    srcKind: src, dstKind: dst,
    srcLabel: src ? ctx.kindLabel(src) : '', dstLabel: dst ? ctx.kindLabel(dst) : '',
  };
}

export function shapeValueEvent(e, mvAccounts, ctx) {
  const kinds = flowKindsOf(e, mvAccounts, ctx);
  return {
    // Raw ids + date stay on the row so a drawer can edit the underlying
    // money_movement and so replays can be traced.
    id: e.id,
    movementId: e.movement_id,
    eventDate: e.event_date,
    kareemShare: Number(e.kareem_share || 0),
    stepId: e.step_id,
    // Who logged the movement that produced this event — 'admin' when an
    // admin acting on the investor's behalf entered it, 'executor' when the
    // investor did, null for agent/legacy rows.
    enteredByRole: e.created_by_role || null,
    enteredByMemberId: e.created_by_member_id || null,
    date: ctx.dateLabel(e.event_date),
    member: e.account_owner_id,
    household: e.enrolled_member_id || ctx.householdOf(e.account_owner_id),
    flow: ctx.kindLabel(kinds.src) + ' → ' + ctx.kindLabel(kinds.dst),
    ...flowFieldsOf(kinds, ctx),
    custody: e.custody === 'pool' ? 'pool' : 'investor',
    amount: Number(e.amount_moved || 0),
    rate: Number(e.value_rate_snapshot || 0),
    value: Number(e.value_generated || 0),
    kareem: Number(e.kareem_share || 0),
    investor: Number(e.investor_share || 0),
  };
}

// A movement that produced NO value event still has to appear: rate-less or
// draft steps post nothing, and pending movements deliberately post nothing
// until confirmed. The ledger promises "every movement", so these render as
// value-less rows (custody null — nobody holds any value).
export function shapeSilentMovement(mv, ctx) {
  const src = ctx.kindOfAccount[mv.source_account_id] || null;
  const dst = ctx.kindOfAccount[mv.dest_account_id] || null;
  const member = ctx.memberOf(mv);
  return {
    id: 'mv-' + mv.id,
    movementId: mv.id,
    eventDate: mv.movement_date || mv.created_at,
    date: ctx.dateLabel(mv.movement_date || mv.created_at),
    member,
    household: ctx.householdOf(member),
    flow: (src && dst) ? ctx.kindLabel(src) + ' → ' + ctx.kindLabel(dst) : 'Transfer',
    ...flowFieldsOf({ src, dst }, ctx),
    custody: null,
    amount: Number(mv.amount || 0),
    rate: 0, value: 0, kareem: 0, investor: 0, kareemShare: 0,
    stepId: null,
    enteredByRole: mv.created_by_role || null,
    enteredByMemberId: mv.created_by_member_id || null,
    movementStatus: mv.status || 'confirmed',
    noValue: true,
  };
}

// events: live value_events rows; movements: money_movements rows (denied
// ones are dropped here); mvAccounts: movement_id → { source_account_id,
// dest_account_id } for the events' movements. Returns rows newest first.
export function buildActivityRows({ events, movements, mvAccounts, ctx }) {
  const rows = (events || []).map((e) => shapeValueEvent(e, mvAccounts || {}, ctx));
  const evented = new Set((events || []).map((e) => e.movement_id).filter(Boolean));
  (movements || [])
    .filter((mv) => mv.id && !evented.has(mv.id) && mv.status !== 'denied')
    .forEach((mv) => rows.push(shapeSilentMovement(mv, ctx)));
  rows.sort((a, b) => String(b.eventDate || '').localeCompare(String(a.eventDate || '')));
  return rows;
}
